import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Filter, ArrowRight, UserCog, X, CheckSquare, Square } from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import { PortfolioTabs } from '../../../components/PortfolioTabs';
import api from '../../../api';
import { encodeLan } from '../../../api';

const STATUS_OPTIONS = [
  ['', 'All statuses'],
  ['new_overdue', 'New Overdue'],
  ['contact_attempted', 'Contact Attempted'],
  ['connected', 'Connected'],
  ['ptp_set', 'PTP Set'],
  ['partially_paid', 'Partially Paid'],
  ['regularized', 'Regularized'],
  ['settled_closed', 'Settled / Closed'],
  ['dispute_hold', 'Dispute Hold'],
  ['escalated_supervisor', 'Escalated'],
  ['legal_initiated', 'Legal Initiated'],
  ['written_off', 'Written Off'],
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.filter(([k]) => k));

function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function statusColor(s) {
  if (s === 'regularized' || s === 'settled_closed') return '#16a34a';
  if (s === 'ptp_set' || s === 'connected') return '#0891b2';
  if (s === 'partially_paid') return '#ca8a04';
  if (s === 'escalated_supervisor' || s === 'legal_initiated' || s === 'written_off') return 'var(--red)';
  return 'var(--navy)';
}

export default function RecoveryCases() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const initialScheme = searchParams.get('scheme') || '';

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const canAssign = currentUser.role === 'owner' || currentUser.role === 'manager';

  const [cases, setCases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [branchFilter, setBranchFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState('');
  const [schemeFilter, setSchemeFilter] = useState(initialScheme);
  const [agentFilter, setAgentFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Bulk-assign state
  const [selected, setSelected] = useState(() => new Set());
  const [assignModal, setAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ targetUserId: '', onlyUnassigned: false, mode: 'agent' });
  const [assigning, setAssigning] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (branchFilter) params.branchId = branchFilter;
      if (agentFilter) params.assignedToUserId = agentFilter;
      const requests = [
        api.get('/recovery/cases', { params }),
        api.get('/branches'),
      ];
      if (canAssign) requests.push(api.get('/users/access').catch(() => ({ data: [] })));
      const [casesRes, brRes, usersRes] = await Promise.all(requests);
      setCases(casesRes.data || []);
      setBranches(brRes.data || []);
      if (canAssign) setUsers(usersRes?.data || []);
      setSelected(new Set()); // clear selection when filters change / reload
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branchFilter, agentFilter, canAssign]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (statusFilter) next.set('status', statusFilter);
    if (schemeFilter) next.set('scheme', schemeFilter);
    setSearchParams(next, { replace: true });
  }, [statusFilter, schemeFilter, setSearchParams]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const filtered = useMemo(() => cases.filter((c) => {
    if (bucketFilter && (c.loan?.dpdBucket || '0') !== bucketFilter) return false;
    if (schemeFilter) {
      const sName = (c.loan?.schemeName || c.loan?.productType || 'Unknown').toLowerCase();
      if (sName !== schemeFilter.toLowerCase()) return false;
    }
    return true;
  }), [cases, bucketFilter, schemeFilter]);

  const schemeOptions = Array.from(new Set(cases.map((c) => c.loan?.schemeName || c.loan?.productType || 'Unknown'))).sort();

  // Branch-restricted agent list for the assign modal. Prefer the active branch filter;
  // otherwise fall back to all branches the user can see (typical for owner).
  const assignableAgents = useMemo(() => {
    return users
      .filter((u) => u.isActive)
      .filter((u) => assignForm.mode === 'manager' ? u.role === 'manager' : u.role === 'agent')
      .filter((u) => !branchFilter || u.branchId === branchFilter);
  }, [users, branchFilter, assignForm.mode]);

  const visibleLans = useMemo(() => filtered.map((c) => c.loanAccountNumber).filter(Boolean), [filtered]);
  const allVisibleSelected = visibleLans.length > 0 && visibleLans.every((lan) => selected.has(lan));
  const someVisibleSelected = visibleLans.some((lan) => selected.has(lan));

  const toggleOne = (lan) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lan)) next.delete(lan); else next.add(lan);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleLans.forEach((lan) => next.delete(lan));
      } else {
        visibleLans.forEach((lan) => next.add(lan));
      }
      return next;
    });
  };

  const openAssignModal = () => {
    setAssignForm({ targetUserId: '', onlyUnassigned: false, mode: 'agent' });
    setAssignModal(true);
  };

  const submitBulkAssign = async (unassign = false) => {
    const lans = Array.from(selected);
    if (lans.length === 0) return;
    if (!unassign && !assignForm.targetUserId) {
      showToast('Pick an agent or manager first');
      return;
    }
    setAssigning(true);
    try {
      const payload = {
        loanAccountNumbers: lans,
        setAgent: assignForm.mode === 'agent',
        setManager: assignForm.mode === 'manager',
        assignedToUserId: assignForm.mode === 'agent' ? (unassign ? null : assignForm.targetUserId) : null,
        supervisingManagerId: assignForm.mode === 'manager' ? (unassign ? null : assignForm.targetUserId) : null,
        onlyUnassigned: assignForm.onlyUnassigned,
      };
      const { data } = await api.post('/recovery/loans/bulk-assign', payload);
      const errCount = (data.errors || []).length;
      let msg = `Updated ${data.updated}/${data.requested}`;
      if (data.skippedAlreadyAssigned) msg += ` · ${data.skippedAlreadyAssigned} already assigned`;
      if (errCount) msg += ` · ${errCount} error${errCount === 1 ? '' : 's'}`;
      showToast(msg);
      if (errCount && data.errors[0]?.error) {
        console.warn('Bulk-assign errors:', data.errors);
      }
      setAssignModal(false);
      setSelected(new Set());
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <OwnerLayout title="Recovery · Cases">
      <PortfolioTabs />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Recovery Case Worklist</h2>
          <p className="text-sm text-gray">{filtered.length} case{filtered.length !== 1 ? 's' : ''}{selected.size > 0 ? ` · ${selected.size} selected` : ''}</p>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--g3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Filter size={12} /> Filters:
          </span>
          <select className="form-input" style={{ width: 'auto', minWidth: '180px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="form-input" style={{ width: 'auto', minWidth: '140px' }} value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)}>
            <option value="">All DPD</option>
            {['0', '1-30', '31-60', '61-90', '91-120', '120+'].map((b) => <option key={b} value={b}>DPD {b}</option>)}
          </select>
          {schemeOptions.length > 1 && (
            <select className="form-input" style={{ width: 'auto', minWidth: '180px' }} value={schemeFilter} onChange={(e) => setSchemeFilter(e.target.value)}>
              <option value="">All schemes</option>
              {schemeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {branches.length > 1 && (
            <select className="form-input" style={{ width: 'auto', minWidth: '160px' }} value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          {canAssign && (
            <select
              className="form-input"
              style={{ width: 'auto', minWidth: '180px' }}
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              title="Filter by assigned agent"
            >
              <option value="">All agents</option>
              {users.filter((u) => u.role === 'agent' && (!branchFilter || u.branchId === branchFilter)).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          {(statusFilter || branchFilter || bucketFilter || schemeFilter || agentFilter) && (
            <button className="btn-outline" onClick={() => { setStatusFilter(''); setBranchFilter(''); setBucketFilter(''); setSchemeFilter(''); setAgentFilter(''); }}>Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
          <ClipboardList size={32} style={{ color: 'var(--g3)', marginBottom: '8px' }} />
          <div>No cases match these filters.</div>
        </div>
      ) : (
        <div className="panel" style={{ padding: 0, overflow: 'hidden', paddingBottom: selected.size > 0 ? '80px' : 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                {canAssign && (
                  <th style={{ ...thStyle, width: 36, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={toggleAllVisible}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: someVisibleSelected ? 'var(--navy)' : 'var(--g3)' }}
                      title={allVisibleSelected ? 'Unselect all visible' : 'Select all visible'}
                    >
                      {allVisibleSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                  </th>
                )}
                <th style={thStyle}>Loan #</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Status</th>
                {canAssign && <th style={thStyle}>Assigned</th>}
                <th style={thStyle}>Bounced</th>
                <th style={thStyle}>DPD</th>
                <th style={thStyle}>Overdue</th>
                <th style={thStyle}>Next Follow-up</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const isSel = selected.has(c.loanAccountNumber);
                const assignedUser = c.assignedToUserId ? userMap[c.assignedToUserId] : null;
                return (
                  <tr
                    key={c.id || c.loanAccountNumber}
                    style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer', background: isSel ? 'rgba(8, 145, 178, 0.06)' : undefined }}
                    onClick={() => navigate(`/owner/recovery/cases/${encodeLan(c.loanAccountNumber)}`)}
                  >
                    {canAssign && (
                      <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => toggleOne(c.loanAccountNumber)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: isSel ? 'var(--navy)' : 'var(--g3)' }}
                          title={isSel ? 'Unselect' : 'Select'}
                        >
                          {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>
                      </td>
                    )}
                    <td style={tdStyle}><strong>{c.loanAccountNumber}</strong></td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.customerName || '—'}</div>
                      <div style={{ fontSize: '11px', color: 'var(--g3)' }}>{c.clientId}{c.customerPhone ? ` · ${c.customerPhone}` : ''}</div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: statusColor(c.status), fontWeight: 600 }}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </td>
                    {canAssign && (
                      <td style={tdStyle}>
                        {assignedUser ? (
                          <span style={{ fontSize: '12px', color: 'var(--navy)' }}>{assignedUser.name}</span>
                        ) : (
                          <span style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 600 }}>Unassigned</span>
                        )}
                      </td>
                    )}
                    <td style={tdStyle}>
                      {(c.loan?.bouncedEmis ?? c.bouncedEmis ?? 0) > 0 ? (
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, color: 'white', background: 'var(--red)' }}>
                          {c.loan?.bouncedEmis ?? c.bouncedEmis} EMI
                        </span>
                      ) : <span style={{ fontSize: '11px', color: 'var(--g3)' }}>—</span>}
                    </td>
                    <td style={tdStyle}>{c.loan?.dpdDays ?? 0}d ({c.loan?.dpdBucket || '0'})</td>
                    <td style={tdStyle}><strong style={{ color: 'var(--red)' }}>{fmtINR(c.loan?.totalOverdueAmount)}</strong></td>
                    <td style={tdStyle}>{c.nextFollowupDate || '—'}</td>
                    <td style={tdStyle}><ArrowRight size={14} color="var(--g3)" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating bulk action bar */}
      {canAssign && selected.size > 0 && (
        <div
          style={{
            position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)',
            background: 'var(--navy)', color: 'white', borderRadius: 12,
            padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'center',
            boxShadow: '0 12px 32px rgba(0,0,0,0.25)', zIndex: 50, fontSize: 13,
            maxWidth: 'calc(100vw - 32px)', flexWrap: 'wrap', justifyContent: 'center',
          }}
        >
          <strong>{selected.size}</strong>
          <span style={{ opacity: 0.85 }}>case{selected.size === 1 ? '' : 's'} selected</span>
          <button className="btn-navy" style={{ background: 'white', color: 'var(--navy)', fontWeight: 600 }} onClick={openAssignModal}>
            <UserCog size={14} style={{ marginRight: 4 }} /> Assign to…
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '6px 10px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Bulk assign modal */}
      {assignModal && (
        <div
          className="owner-modal-overlay open"
          onClick={(e) => { if (e.target === e.currentTarget && !assigning) setAssignModal(false); }}
        >
          <div className="owner-modal" style={{ width: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-head">
              <div className="modal-icon"><UserCog size={16} /></div>
              <div>
                <div className="modal-title">Bulk Assign ({selected.size} case{selected.size === 1 ? '' : 's'})</div>
                <div className="modal-subtitle">Reassign all selected loans in one go</div>
              </div>
              <button className="modal-close" onClick={() => !assigning && setAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {!branchFilter && branches.length > 1 && (
                <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 6, marginBottom: 10 }}>
                  Tip: filter by branch first — agents only handle loans in their own branch, so cross-branch picks will be rejected.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Assign as</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="radio" name="assignMode" value="agent" checked={assignForm.mode === 'agent'} onChange={() => setAssignForm((p) => ({ ...p, mode: 'agent', targetUserId: '' }))} />
                    Field agent
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="radio" name="assignMode" value="manager" checked={assignForm.mode === 'manager'} onChange={() => setAssignForm((p) => ({ ...p, mode: 'manager', targetUserId: '' }))} />
                    Supervising manager
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{assignForm.mode === 'manager' ? 'Supervising manager' : 'Field agent'}</label>
                <select
                  className="form-input"
                  value={assignForm.targetUserId}
                  onChange={(e) => setAssignForm((p) => ({ ...p, targetUserId: e.target.value }))}
                >
                  <option value="">Select {assignForm.mode === 'manager' ? 'manager' : 'agent'}…</option>
                  {assignableAgents.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.employeeId}){u.branchName ? ` · ${u.branchName}` : ''}
                    </option>
                  ))}
                </select>
                {assignableAgents.length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                    No active {assignForm.mode === 'manager' ? 'managers' : 'agents'} found {branchFilter ? 'in this branch' : ''}.
                  </div>
                )}
              </div>
              {assignForm.mode === 'agent' && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={assignForm.onlyUnassigned}
                      onChange={(e) => setAssignForm((p) => ({ ...p, onlyUnassigned: e.target.checked }))}
                    />
                    Only assign cases that are currently unassigned (skip already-assigned)
                  </label>
                </div>
              )}
              <div className="flex justify-between gap-2 mt-2">
                <button
                  type="button"
                  className="btn-outline"
                  style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                  disabled={assigning}
                  onClick={() => {
                    if (window.confirm(`Unassign ${assignForm.mode === 'manager' ? 'manager' : 'agent'} from ${selected.size} selected case${selected.size === 1 ? '' : 's'}?`)) {
                      submitBulkAssign(true);
                    }
                  }}
                  title="Remove current assignment from all selected cases"
                >
                  Unassign all
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-outline" disabled={assigning} onClick={() => setAssignModal(false)}>Cancel</button>
                  <button type="button" className="btn-navy" disabled={assigning || !assignForm.targetUserId} onClick={() => submitBulkAssign(false)}>
                    {assigning ? 'Assigning…' : `Assign ${selected.size}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`au-toast ${toast.show ? 'show' : ''}`} style={{ bottom: selected.size > 0 ? 80 : 24 }}>{toast.msg}</div>
    </OwnerLayout>
  );
}

const thStyle = { textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: 'var(--g4)', letterSpacing: '0.04em' };
const tdStyle = { padding: '10px 14px', verticalAlign: 'middle' };

